import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Plane } from 'lucide-react';
import { toast } from 'sonner';

const Airports = () => {
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAirport, setEditingAirport] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    city: '',
    country: '',
    active: true
  });

  useEffect(() => {
    fetchAirports();
  }, []);

  const fetchAirports = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/airports`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAirports(data);
      } else {
        toast.error('Failed to fetch airports');
      }
    } catch (error) {
      toast.error('Error fetching airports');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingAirport
      ? `${process.env.REACT_APP_BACKEND_URL}/api/airports/${editingAirport.id}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/airports`;
    
    const method = editingAirport ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingAirport ? 'Airport updated successfully' : 'Airport created successfully');
        fetchAirports();
        setShowDialog(false);
        resetForm();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to save airport');
      }
    } catch (error) {
      toast.error('Error saving airport');
    }
  };

  const handleEdit = (airport) => {
    setEditingAirport(airport);
    setFormData({
      code: airport.code,
      name: airport.name,
      city: airport.city || '',
      country: airport.country || '',
      active: airport.active
    });
    setShowDialog(true);
  };

  const handleDelete = async (airportId) => {
    if (!window.confirm('Are you sure you want to delete this airport?')) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/airports/${airportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Airport deleted successfully');
        fetchAirports();
      } else {
        toast.error('Failed to delete airport');
      }
    } catch (error) {
      toast.error('Error deleting airport');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      city: '',
      country: '',
      active: true
    });
    setEditingAirport(null);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#FFFFFF' }}>
            Airports
          </h1>
          <p className="text-sm mt-1" style={{ color: '#B0B0B0' }}>
            Manage airports where VIP services are available
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowDialog(true);
          }}
          style={{ background: '#1E3A8A', color: '#FFFFFF' }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Airport
        </Button>
      </div>

      {loading ? (
        <div className="text-center" style={{ color: '#B0B0B0' }}>Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {airports.map((airport) => (
            <Card key={airport.id} style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Plane className="h-5 w-5" style={{ color: '#60A5FA' }} />
                    <span style={{ color: '#FFFFFF' }}>{airport.code}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(airport)}
                      style={{ color: '#60A5FA' }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(airport.id)}
                      style={{ color: '#EF4444' }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <div className="text-sm font-medium" style={{ color: '#E8E8E8' }}>
                      {airport.name}
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#B0B0B0' }}>City:</span>
                    <span style={{ color: '#E8E8E8' }}>{airport.city || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#B0B0B0' }}>Country:</span>
                    <span style={{ color: '#E8E8E8' }}>{airport.country || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span style={{ color: '#B0B0B0' }}>Status:</span>
                    <span 
                      className="px-2 py-1 rounded text-xs"
                      style={{ 
                        background: airport.active ? '#10B981' : '#6B7280',
                        color: '#FFFFFF'
                      }}
                    >
                      {airport.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent style={{ background: '#1E2A4A', borderColor: '#4A5A8A' }}>
          <DialogHeader>
            <DialogTitle style={{ color: '#FFFFFF' }}>
              {editingAirport ? 'Edit Airport' : 'Add New Airport'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Airport Code (IATA) *</Label>
                  <Input
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., BRU"
                    maxLength={3}
                    required
                    disabled={!!editingAirport}
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Status</Label>
                  <select
                    value={formData.active ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
                    className="w-full px-3 py-2 rounded"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8', border: '1px solid' }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <Label style={{ color: '#E8E8E8' }}>Airport Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Brussels Airport"
                  required
                  style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label style={{ color: '#E8E8E8' }}>City</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Brussels"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                </div>
                <div>
                  <Label style={{ color: '#E8E8E8' }}>Country</Label>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., Belgium"
                    style={{ background: '#0A1128', borderColor: '#4A5A8A', color: '#E8E8E8' }}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
                style={{ borderColor: '#4A5A8A', color: '#E8E8E8' }}
              >
                Cancel
              </Button>
              <Button type="submit" style={{ background: '#1E3A8A', color: '#FFFFFF' }}>
                {editingAirport ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Airports;
